import React, { useEffect, useState } from "react";
import priceApprovalService from "../services/priceApprovalService";

const STATUS_COLORS = {
  priced:         { bg: "#fffbe6", color: "#b45309", border: "#fde68a" },
  price_accepted: { bg: "#f0fdf4", color: "#166534", border: "#bbf7d0" },
  offer_rejected: { bg: "#fef2f2", color: "#991b1b", border: "#fecaca" },
};

const PriceAcceptanceTab = () => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [actionId, setActionId]       = useState(null);
  const [error, setError]             = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await priceApprovalService.getPricedInspections();
      setInspections(data);
    } catch (err) {
      console.error("PriceAcceptanceTab:", err);
      setError("Failed to load price offers. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAccept = async (sellRequestId) => {
    if (!window.confirm("Accept this price offer? This will initiate the pickup process.")) return;
    setActionId(sellRequestId);
    try {
      await priceApprovalService.acceptPrice(sellRequestId);
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || "Failed to accept. Please try again.");
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async (sellRequestId) => {
    if (!window.confirm("Reject this offer? Our team may reach out with a revised price.")) return;
    setActionId(sellRequestId);
    try {
      await priceApprovalService.rejectPrice(sellRequestId);
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || "Failed to reject. Please try again.");
    } finally {
      setActionId(null);
    }
  };

  if (loading) {
    return (
      <div className="tab-content">
        <div className="tab-header">
          <h2 className="tab-title">💰 Price Approval</h2>
          <p className="tab-subtitle">Loading your price offers…</p>
        </div>
        <div className="state-center">
          <div className="spinner-large" />
          <p>Fetching offers…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <div className="tab-header">
          <h2 className="tab-title">💰 Price Approval</h2>
        </div>
        <div className="state-center">
          <span style={{ fontSize: 48 }}>⚠️</span>
          <p style={{ color: "#c30000" }}>{error}</p>
          <button className="btn-primary" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!inspections.length) {
    return (
      <div className="tab-content">
        <div className="tab-header">
          <h2 className="tab-title">💰 Price Approval</h2>
          <p className="tab-subtitle">Review and respond to price offers from our team</p>
        </div>
        <div className="empty-state">
          <div className="empty-icon">💰</div>
          <h3>No Price Offers Yet</h3>
          <p>
            Once our team inspects and evaluates your car, a price offer will
            appear here for you to accept or decline.
          </p>
        </div>
      </div>
    );
  }

  const pendingCount = inspections.filter(i => i.displayStatus === "priced").length;

  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2 className="tab-title">💰 Price Approval</h2>
        <p className="tab-subtitle">
          {pendingCount > 0
            ? `${pendingCount} offer${pendingCount > 1 ? "s" : ""} waiting for your response`
            : "All offers have been responded to"}
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {inspections.map((inspection) => {
          const sr       = inspection.sell_request || {};
          const carBrand = sr.brand    || inspection.car_brand || "—";
          const carModel = sr.model    || inspection.model     || "—";
          const carYear  = sr.year     || inspection.year      || "";
          const carKm    = sr.km_driven;
          const priceExp = sr.price_expectation;

          const offeredPrice = inspection.estimated_price;
          const condNote     = inspection.condition_report;

          // Use displayStatus (resolved from sell_request.status + inspection.status)
          const displayStatus = inspection.displayStatus || inspection.status;
          const isPriced   = displayStatus === "priced";
          const isAccepted = displayStatus === "price_accepted";
          const isRejected = displayStatus === "offer_rejected";
          const isActing   = actionId === inspection.sell_request_id;

          const statusStyle = STATUS_COLORS[displayStatus] || STATUS_COLORS.priced;
          const statusLabel = isPriced   ? "Awaiting Your Response"
                            : isAccepted ? "Price Accepted ✓"
                            :              "Offer Rejected";

          return (
            <div
              key={inspection.id}
              style={{
                background: "#fff",
                border: `1.5px solid ${isPriced ? "#FFD60A" : statusStyle.border}`,
                borderLeft: `5px solid ${isPriced ? "#f59e0b" : statusStyle.color}`,
                borderRadius: 18,
                overflow: "hidden",
                boxShadow: isPriced
                  ? "0 4px 20px rgba(255,214,10,0.18)"
                  : "0 2px 10px rgba(0,0,0,0.06)",
              }}
            >
              {/* ── Header ── */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "18px 24px",
                borderBottom: "1px solid #f5f5f5",
                flexWrap: "wrap",
                gap: 12,
              }}>
                <div>
                  <h3 style={{ margin: "0 0 5px", fontSize: 20, fontWeight: 800, color: "#003566" }}>
                    {carBrand} {carModel}{" "}
                    {carYear && (
                      <span style={{ color: "#999", fontWeight: 500, fontSize: 15 }}>({carYear})</span>
                    )}
                  </h3>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 13, color: "#888", fontWeight: 500 }}>
                    {carKm    && <span>🛣️ {Number(carKm).toLocaleString("en-IN")} km</span>}
                    {priceExp && <span>💬 Expected: ₹{Number(priceExp).toLocaleString("en-IN")}</span>}
                    <span>🔖 {inspection.booking_reference || `#${inspection.id}`}</span>
                  </div>
                </div>
                <span style={{
                  padding: "6px 16px",
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  background: statusStyle.bg,
                  color: statusStyle.color,
                  border: `1.5px solid ${statusStyle.border}`,
                  whiteSpace: "nowrap",
                }}>
                  {statusLabel}
                </span>
              </div>

              {/* ── Price Strip ── */}
              <div style={{
                padding: "24px",
                background: isPriced
                  ? "linear-gradient(135deg, #fffbe6, #fef9c3)"
                  : isAccepted
                  ? "linear-gradient(135deg, #f0fdf4, #dcfce7)"
                  : "linear-gradient(135deg, #fef2f2, #fee2e2)",
              }}>
                <p style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {isPriced ? "🔥 Offered Price" : isAccepted ? "✅ Accepted Price" : "❌ Rejected Price"}
                </p>
                <p style={{ margin: 0, fontSize: 40, fontWeight: 900, color: "#003566", lineHeight: 1, letterSpacing: "-1px" }}>
                  ₹{offeredPrice ? Number(offeredPrice).toLocaleString("en-IN") : "—"}
                </p>
              {/*  {priceExp && offeredPrice && (
                  <p style={{ margin: "8px 0 0", fontSize: 13, color: "#555", fontWeight: 500 }}>
                    {Number(offeredPrice) >= Number(priceExp)
                      ? `🎉 ₹${(Number(offeredPrice) - Number(priceExp)).toLocaleString("en-IN")} above your expected price`
                      : `📉 ₹${(Number(priceExp) - Number(offeredPrice)).toLocaleString("en-IN")} below your expected price`}
                  </p>
                )}*/}
              </div>

              {/* ── Admin's Assessment ── */}
              {condNote && (
                <div style={{ padding: "16px 24px", borderTop: "1px solid #f5f5f5" }}>
                  <p style={{ margin: "0 0 7px", fontSize: 11, fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    📋 Admin's Assessment
                  </p>
                  <p style={{
                    margin: 0, fontSize: 14, color: "#444", lineHeight: 1.65,
                    background: "#f8f9fa", padding: "12px 16px", borderRadius: 10, border: "1px solid #eee",
                  }}>
                    {condNote}
                  </p>
                </div>
              )}

              {/* ── Action Buttons (only for status === "priced") ── */}
              {isPriced && (
                <div style={{ display: "flex", gap: 12, padding: "16px 24px 20px", borderTop: "1px solid #f0f0f0" }}>
                  <button
                    onClick={() => handleAccept(inspection.sell_request_id)}
                    disabled={isActing}
                    style={{
                      flex: 1, padding: "13px 20px",
                      background: isActing ? "#e5e7eb" : "linear-gradient(135deg, #FFD60A, #FFC700)",
                      color: isActing ? "#9ca3af" : "#003566",
                      border: "none", borderRadius: 10, fontSize: 14, fontWeight: 800,
                      cursor: isActing ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    }}
                  >
                    {isActing ? <><span className="spinner-small" /> Processing…</> : "✅ Accept Price Offer"}
                  </button>

                  <button
                    onClick={() => handleReject(inspection.sell_request_id)}
                    disabled={isActing}
                    style={{
                      flex: 1, padding: "13px 20px",
                      background: "#fff", color: "#dc2626",
                      border: "1.5px solid #dc2626", borderRadius: 10, fontSize: 14, fontWeight: 800,
                      cursor: isActing ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      opacity: isActing ? 0.5 : 1,
                    }}
                  >
                    ❌ Reject Offer
                  </button>
                </div>
              )}

              {/* ── Post-decision banner ── */}
              {(isAccepted || isRejected) && (
                <div style={{
                  padding: "13px 24px", borderTop: "1px solid #f0f0f0",
                  background: isAccepted ? "#f0fdf4" : "#fef2f2",
                  fontSize: 13, fontWeight: 600,
                  color: isAccepted ? "#166534" : "#991b1b",
                }}>
                  {isAccepted
                    ? "🚗 Great! Our team will contact you shortly to arrange vehicle pickup."
                    : "📞 We've noted your decision. Our team may reach out with a revised offer."}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PriceAcceptanceTab;